<script setup lang="ts">
import { usePhoneStore } from '@/stores/phone';
import { nextTick, ref, watch } from 'vue';
import logger from '@/logger';
import {
  closeSidebar,
  openSidebar,
  SIDEBAR,
} from '@/app/components/sidebar/sidebar';
import { DATA_QA } from './phone-sidebar.constants';
import { DtTab, DtTabGroup, DtTabPanel } from '@dialpad/dialtone/vue3/lib/tab';
import { DtIcon } from '@dialpad/dialtone/vue3/lib/icon';
import { DtButton } from '@dialpad/dialtone/vue3/lib/button';
import { DATA_QA as SIDEBAR_WRAPPER_DATA_QA } from '@/app/components/sidebar/sidebar-wrapper.constants';
import { DtSkeleton } from '@dialpad/dialtone/vue3/lib/skeleton';
import { DtListItemGroup } from '@dialpad/dialtone/vue3/lib/list-item-group';
import { DtListItem } from '@dialpad/dialtone/vue3/lib/list-item';
import { DtDropdown } from '@dialpad/dialtone/vue3/lib/dropdown';
import PhoneDetails from '../PhoneDetails/PhoneDetails.vue';
import PhoneChangeLog from '../PhoneChangeLog/PhoneChangeLog.vue';
import { MODAL, openModal } from '@/app/components/modal/modals';

type Props = {
  id?: string;
  tab?: string;
};

const props = defineProps<Props>();

const log = logger.getChild('PhoneSidebar');
const phoneStore = usePhoneStore();

watch(
  () => props.id,
  async (value) => {
    if (!value) {
      log.warn('No id provided');
      await closeSidebar();
      return;
    }
  },
  { immediate: true },
);
const phone = phoneStore.watch(() => props.id ?? null, 'PhoneSidebar');

const selectedTab = ref('details');

watch(
  () => props.tab,
  (newTab) => {
    if (newTab === 'change-log' || newTab === 'details') {
      selectedTab.value = newTab;
    }
  },
  { immediate: true },
);
const onTabChange = ({ selected }: { selected: string }) => {
  log.debug('Tab changed', selected);
  selectedTab.value = selected;
};

const onEdit = () => {
  if (!phone.value.item?.e164) {
    log.error('No phone e164');
    return;
  }
  void openSidebar(SIDEBAR.PHONE_EDIT, { id: phone.value.item.e164 });
};

const resolvers = ref<((data: unknown) => void)[]>([]);
const waitForDropdownClose = async (close?: () => void) => {
  if (typeof close === 'function') {
    close();
    await new Promise((resolve) => {
      resolvers.value.push(resolve);
    });
    await nextTick();
  }
};

const onMenuOpened = async (value: boolean) => {
  if (value) return;
  for (const resolve of resolvers.value) {
    resolve(null);
  }
};

const onDeleteItem = async (id?: string, close?: () => void) => {
  log.debug('Deleting CSV', id);

  if (!id) {
    log.error('No id provided');
    return;
  }
  await waitForDropdownClose(close);
  await openModal(MODAL.DELETE_DNC_RECORD, { id: id });
};

const onEditItem = async (id?: string, close?: () => void) => {
  log.debug('Editing phone', id);

  if (!id) {
    log.error('No id provided');
    return;
  }
  await waitForDropdownClose(close);
  await openSidebar(SIDEBAR.PHONE_EDIT, { id: id });
};
</script>

<template>
  <div :data-qa="DATA_QA.ROOT" class="d-h100p d-d-flex d-fd-column d-of-hidden">
    <div
      class="d-d-flex d-ai-center d-jc-space-between d-pt12 d-pb8 d-pl12 d-pr8"
    >
      <h3 v-if="phone.item" class="d-fc-primary d-to-ellipsis d-headline--l">
        {{ phone.item.formattedPhone }}
      </h3>
      <DtSkeleton v-else :text-option="{ type: `heading`, width: `240px` }" />
      <div class="d-d-flex">
        <DtDropdown class="d-mr8" placement="bottom-end" @opened="onMenuOpened">
          <template #anchor>
            <DtButton
              importance="clear"
              kind="muted"
              :aria-label="$t('CSV_ITEM_MENU_ARIA_LABEL')"
              :data-qa="DATA_QA.MENU"
              :circle="true"
            >
              <template #icon>
                <DtIcon name="more-vertical" size="400" />
              </template>
            </DtButton>
          </template>
          <template #list="{ close }">
            <DtListItemGroup
              heading-class="d-py4 d-px8 d-fw-semibold d-c-default"
              :data-qa="DATA_QA.MENU_LIST"
            >
              <DtListItem
                navigation-type="tab"
                :data-qa="DATA_QA.EDIT"
                @click="onEditItem(phone.item?.e164, close)"
              >
                {{ $t('PHONE_SIDEBAR_EDIT') }}
              </DtListItem>
              <DtListItem
                navigation-type="tab"
                class="d-fc-critical"
                :data-qa="DATA_QA.DELETE_ITEM"
                @click="onDeleteItem(phone.item?.e164, close)"
              >
                {{ $t('PHONE_SIDEBAR_DELETE') }}
              </DtListItem>
            </DtListItemGroup>
          </template>
        </DtDropdown>
        <DtButton
          :data-qa="SIDEBAR_WRAPPER_DATA_QA.CLOSE"
          circle
          kind="muted"
          importance="clear"
          @click="closeSidebar"
        >
          <template #icon>
            <DtIcon name="close" size="300" />
          </template>
        </DtButton>
      </div>
    </div>
    <DtTabGroup
      :selected="selectedTab"
      class="d-fl-grow1 d-of-hidden"
      @change="onTabChange"
    >
      <template #tabs>
        <DtTab id="details" panel-id="details">
          {{ $t('PHONE_SIDEBAR_DETAILS') }}
        </DtTab>
        <DtTab id="change-log" panel-id="change-log">
          {{ $t('PHONE_SIDEBAR_CHANGE_LOG') }}
        </DtTab>
      </template>
      <div class="d-h100p d-d-flex d-fd-column d-of-y-auto">
        <DtTabPanel id="details" tab-id="details">
          <PhoneDetails
            :number="id ?? null"
            @open-changelog="selectedTab = 'change-log'"
          />
        </DtTabPanel>
        <DtTabPanel id="change-log" tab-id="change-log">
          <PhoneChangeLog :phone-id="phone.item?.id ?? null" />
        </DtTabPanel>
      </div>
    </DtTabGroup>
    <div class="d-d-flex d-jc-space-around d-ai-center d-p16 d-bt d-bc-subtle">
      <DtButton
        :data-qa="DATA_QA.EDIT"
        importance="clear"
        :disabled="phone.loading"
        @click="onEdit"
      >
        {{ $t('PHONE_SIDEBAR_EDIT') }}
      </DtButton>
    </div>
  </div>
</template>
